/**
 * Login Page - Client Portal
 *
 * Uses Firebase Authentication for secure login
 * No more hardcoded credentials!
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import styles from './Login.module.css';

export function LoginPage() {
    const navigate = useNavigate();
    const { signIn, clientUser, loading: authLoading, error: authError, clearError } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Redirect if already logged in
    useEffect(() => {
        if (clientUser) {
            navigate(`/${clientUser.clientId}`);
        }
    }, [clientUser, navigate]);

    // Sync auth error
    useEffect(() => {
        if (authError) {
            setError(authError);
        }
    }, [authError]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        clearError();
        setLoading(true);

        try {
            await signIn(email, password);
            // Navigation handled by useEffect when clientUser updates
        } catch (err) {
            // Error already set by auth context
            if (err instanceof Error) {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    // Show loading while checking auth state
    if (authLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingBox}>
                    <div className={styles.spinner}></div>
                    <p>Memuat...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.themeToggleWrapper}>
                <ThemeToggle />
            </div>
            <div className={styles.loginBox}>
                {/* Logo */}
                <div className={styles.logo}>
                    <span className={styles.logoIcon}>🏪</span>
                    <h1>Spesialis Client</h1>
                    <p>Portal untuk mitra Spesialis App</p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleLogin} className={styles.form}>
                    {error && (
                        <div className={styles.error}>
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className={styles.field}>
                        <label htmlFor="email">Email</label>
                        <div className={styles.inputWrapper}>
                            <User size={18} className={styles.inputIcon} />
                            <input
                                id="email"
                                type="email"
                                placeholder="email@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label htmlFor="password">Password</label>
                        <div className={styles.inputWrapper}>
                            <Lock size={18} className={styles.inputIcon} />
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <Button type="submit" loading={loading} className={styles.submitBtn}>
                        Masuk
                    </Button>
                </form>

                {/* Info */}
                <div className={styles.info}>
                    <p>
                        Belum punya akun? Hubungi tim Spesialis untuk registrasi.
                    </p>
                </div>
            </div>

            {/* Footer */}
            <footer className={styles.footer}>
                <p>© 2026 Spesialis App. All rights reserved.</p>
            </footer>
        </div>
    );
}
